(() => {
class HoneycombConfigurator extends HTMLElement {
  connectedCallback() {
    if (this.dataset.ready === 'true') return;

    const manifestNode = this.querySelector('[data-hc-manifest]');
    if (!manifestNode) return;

    try {
      this.manifest = JSON.parse(manifestNode.textContent);
    } catch (error) {
      console.error('Honeycomb product manifest could not be read.', error);
      return;
    }

    this.preloadCache = new Map();
    this.warmImageQueue = [];
    this.warmImageQueued = new Set();
    this.mediaRequest = 0;
    this.galleryRoles = Array.from(this.querySelectorAll('[data-hc-thumb-role]'))
      .map((button) => button.dataset.hcThumbRole)
      .filter(Boolean);
    this.gallerySwipeStart = null;
    this.dataset.ready = 'true';
    this.onChange = this.onChange.bind(this);
    this.onClick = this.onClick.bind(this);
    this.onInput = this.onInput.bind(this);
    this.onFocusIn = this.onFocusIn.bind(this);
    this.onPointerOver = this.onPointerOver.bind(this);
    this.onKeyDown = this.onKeyDown.bind(this);
    this.onPointerDown = this.onPointerDown.bind(this);
    this.onPointerMove = this.onPointerMove.bind(this);
    this.onPointerUp = this.onPointerUp.bind(this);
    this.onDialogClose = this.onDialogClose.bind(this);
    this.onSubmit = this.onSubmit.bind(this);
    this.onAddressSubmit = this.onAddressSubmit.bind(this);
    this.onCartLinesUpdate = this.onCartLinesUpdate.bind(this);
    this.onDiscountUpdate = this.onDiscountUpdate.bind(this);
    this.onDestinationDocumentPointerDown = this.onDestinationDocumentPointerDown.bind(this);
    this.addEventListener('change', this.onChange);
    this.addEventListener('click', this.onClick);
    this.addEventListener('input', this.onInput);
    this.addEventListener('focusin', this.onFocusIn);
    this.addEventListener('pointerover', this.onPointerOver);
    this.addEventListener('keydown', this.onKeyDown);
    this.addEventListener('pointerdown', this.onPointerDown);
    this.addEventListener('pointermove', this.onPointerMove);
    this.addEventListener('pointerup', this.onPointerUp);
    this.addEventListener('pointercancel', this.onPointerUp);
    this.querySelector('[data-hc-product-form]')?.addEventListener('submit', this.onSubmit, true);
    document.addEventListener('shopify:cart:lines-update', this.onCartLinesUpdate);
    document.addEventListener('shopify:cart:discount-update', this.onDiscountUpdate);
    this.querySelectorAll('.hcp-dialog').forEach((dialog) => dialog.addEventListener('close', this.onDialogClose));
    const addressForm = this.querySelector('[data-hc-address-form]');
    addressForm?.addEventListener('submit', this.onAddressSubmit);
    this.installEnglishAddressValidation(addressForm);

    this.defaultPriceText = this.querySelector('[data-hc-price]')?.textContent.trim() || '';
    this.cartActions = this.querySelector('.hcp-cart-actions');
    this.stickyCart = this.querySelector('[data-hc-sticky-cart]');
    this.submitButton = this.querySelector('.hcp-submit');
    this.setupStickyCartObserver();
    if (this.submitButton) {
      this.stickyStateObserver = new MutationObserver(() => this.syncStickyCart());
      this.stickyStateObserver.observe(this.submitButton, {
        attributes: true,
        attributeFilter: ['disabled', 'aria-busy', 'aria-disabled'],
        childList: true,
        subtree: true,
      });
    }
    const appBlocks = this.querySelector('[data-hc-app-blocks]');
    if (appBlocks) {
      this.pricingObserver = new MutationObserver(() => {
        this.syncPricing();
        this.syncPriceCalculator();
      });
      this.pricingObserver.observe(appBlocks, { childList: true, subtree: true });
    }

    this.dataset.unit = 'millimeter';
    this.initializeRulers();
    this.restoreConfigurationFromUrl();
    this.setupDestinationPicker();
    this.syncSeries();
    this.syncSelection();
    this.syncDimensions();
    this.syncStickyCart();
    this.preloadLikelyImages();
    this.openCheckoutFromUrl();
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.onChange);
    this.removeEventListener('click', this.onClick);
    this.removeEventListener('input', this.onInput);
    this.removeEventListener('focusin', this.onFocusIn);
    this.removeEventListener('pointerover', this.onPointerOver);
    this.removeEventListener('keydown', this.onKeyDown);
    this.removeEventListener('pointerdown', this.onPointerDown);
    this.removeEventListener('pointermove', this.onPointerMove);
    this.removeEventListener('pointerup', this.onPointerUp);
    this.removeEventListener('pointercancel', this.onPointerUp);
    this.querySelector('[data-hc-product-form]')?.removeEventListener('submit', this.onSubmit, true);
    document.removeEventListener('shopify:cart:lines-update', this.onCartLinesUpdate);
    document.removeEventListener('shopify:cart:discount-update', this.onDiscountUpdate);
    document.removeEventListener('pointerdown', this.onDestinationDocumentPointerDown);
    this.querySelectorAll('.hcp-dialog').forEach((dialog) => dialog.removeEventListener('close', this.onDialogClose));
    this.querySelector('[data-hc-address-form]')?.removeEventListener('submit', this.onAddressSubmit);
    this.pricingObserver?.disconnect();
    this.stickyStateObserver?.disconnect();
    if (this.stickyCartObserver) this.stickyCartObserver.disconnect();
    if (this.onStickyCartViewportChange) {
      this.stickyCartScrollTarget?.removeEventListener('scroll', this.onStickyCartViewportChange);
      window.removeEventListener('resize', this.onStickyCartViewportChange);
    }
    if (this.preloadIdle && 'cancelIdleCallback' in window) cancelIdleCallback(this.preloadIdle);
    if (this.preloadTimer) clearTimeout(this.preloadTimer);
    if (this.marketEstimateTimer) clearTimeout(this.marketEstimateTimer);
    this.marketEstimateController?.abort();
    delete this.dataset.ready;
  }

  installEnglishAddressValidation(form) {
    if (!form || form.dataset.englishValidationReady === 'true') return;
    form.dataset.englishValidationReady = 'true';
    form.addEventListener('invalid', (event) => {
      const field = event.target;
      if (!(field instanceof HTMLInputElement)) return;
      field.setCustomValidity('');
      if (field.validity.valueMissing) {
        field.setCustomValidity('Please fill out this field.');
      } else if (field.validity.typeMismatch && field.type === 'email') {
        field.setCustomValidity('Please enter a valid email address.');
      }
    }, true);
    form.addEventListener('input', (event) => {
      if (event.target instanceof HTMLInputElement) event.target.setCustomValidity('');
    });
  }

  onInput(event) {
    if (event.target.closest('[data-hc-address-form]')) {
      const dialog = this.querySelector('[data-hc-draft-checkout-dialog]');
      if (dialog?.dataset.confirmed === 'true') this.resetDraftCheckoutConfirmation();
      return;
    }

    if (event.target.matches('input[name="properties[Width (cm)]"], input[name="properties[Height (cm)]"]')) {
      this.syncPricing();
      this.syncFreight();
    }
  }

  onDiscountUpdate() {
    if (this.querySelector('[data-hc-dhl-zone]')?.value) this.requestMarketEstimate();
  }

  onChange(event) {
    if (event.target.matches('[data-hc-dhl-zone]')) {
      this.syncDestinationPicker();
      this.syncFreight();
      return;
    }

    const input = event.target.closest('input[type="radio"]');
    if (!input) return;

    if (input.name === 'hc-series') {
      this.syncSeries();
      this.syncSelection();
      return;
    }

    if (input.name.startsWith('hc-color-')) {
      this.syncSelection();
      return;
    }

    if (input.name === 'hc-rail') {
      this.syncSelection();
      return;
    }

    if (input.name === 'hc-direction') {
      this.syncSelection();
    }
  }

  onFocusIn(event) {
    this.prefetchSelectionIntent(event);
    const ruler = event.target.closest('[data-hc-ruler]');
    if (ruler?.dataset.hcMeasurePoint) {
      this.setActiveMeasurementPoint(ruler.dataset.hcMeasurePoint);
    }
  }

  onPointerOver(event) {
    this.prefetchSelectionIntent(event);
  }

  onClick(event) {
    if (event.target.closest('[data-hc-sticky-scroll-top]')) {
      event.preventDefault();
      this.scrollToConfiguratorTop();
      return;
    }

    const checkoutTrigger = event.target.closest('[data-hc-checkout]');
    if (checkoutTrigger) {
      if (this.openServerPricedCheckout()) {
        event.preventDefault();
        return;
      }
      if (checkoutTrigger.classList.contains('is-pending')) {
        event.preventDefault();
        return;
      }
      this.startCheckoutNavigation(checkoutTrigger);
      return;
    }

    if (event.target.closest('[data-hc-close-draft-checkout]')) {
      event.preventDefault();
      this.querySelector('[data-hc-draft-checkout-dialog]')?.close();
      return;
    }

    const destinationTrigger = event.target.closest('[data-hc-destination-trigger]');
    if (destinationTrigger) {
      event.preventDefault();
      if (this.destinationPicker?.classList.contains('is-open')) {
        this.closeDestinationPicker({ restoreFocus: false });
      } else {
        this.openDestinationPicker();
      }
      return;
    }

    const destinationOption = event.target.closest('[data-hc-destination-option]');
    if (destinationOption) {
      event.preventDefault();
      this.selectDestinationOption(destinationOption);
      return;
    }

    if (event.target.closest('[data-hc-destination-dismiss]')) {
      event.preventDefault();
      this.closeDestinationPicker({ restoreFocus: true });
      return;
    }

    const cartTrigger = event.target.closest('[data-hc-open-cart]');
    if (cartTrigger) {
      this.openCartDrawer();
      return;
    }

    const galleryPrevious = event.target.closest('[data-hc-gallery-prev]');
    if (galleryPrevious) {
      this.moveGallery(-1);
      return;
    }

    const galleryNext = event.target.closest('[data-hc-gallery-next]');
    if (galleryNext) {
      this.moveGallery(1);
      return;
    }

    const directionTrigger = event.target.closest('[data-hc-open-direction]');
    if (directionTrigger) {
      this.openDialog(this.querySelector('[data-hc-direction-dialog]'), directionTrigger);
      return;
    }

    const measurementTrigger = event.target.closest('[data-hc-open-measurement]');
    if (measurementTrigger) {
      this.prepareMeasurementGuide();
      this.openDialog(this.querySelector('[data-hc-measurement-dialog]'), measurementTrigger);
      return;
    }

    const closeTrigger = event.target.closest('[data-hc-close-dialog]');
    if (closeTrigger) {
      this.closeDialog(closeTrigger.closest('.hcp-dialog'));
      return;
    }

    const directionChoice = event.target.closest('[data-hc-select-direction]');
    if (directionChoice) {
      const input = this.querySelector(`[name="hc-direction"][value="${directionChoice.dataset.hcSelectDirection}"]`);
      if (input) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
      this.closeDialog(directionChoice.closest('.hcp-dialog'));
      return;
    }

    const rulerStep = event.target.closest('[data-hc-ruler-step]');
    if (rulerStep) {
      this.stepRuler(rulerStep.closest('[data-hc-ruler]'), Number(rulerStep.dataset.hcRulerStep));
      return;
    }

    const unitTrigger = event.target.closest('[data-hc-unit]');
    if (unitTrigger) {
      this.setUnit(unitTrigger.dataset.hcUnit);
      return;
    }

    const measurementApply = event.target.closest('[data-hc-apply-measurement]');
    if (measurementApply) {
      this.applyMeasurementGuide();
      return;
    }

    const openDialog = event.target.matches('.hcp-dialog[open]') ? event.target : null;
    if (openDialog) {
      this.closeDialog(openDialog);
      return;
    }

    const thumbnail = event.target.closest('[data-hc-thumb-role]');
    if (!thumbnail) return;
    const role = thumbnail.dataset.hcThumbRole;
    this.showMedia(role, this.galleryDirectionFor(role));
  }

  onKeyDown(event) {
    if (this.handleDestinationKeyDown(event)) return;

    if (event.key === 'Escape') {
      const dialog = this.querySelector('.hcp-dialog[open]');
      if (dialog) this.closeDialog(dialog);
      return;
    }

    const ruler = event.target.closest('[data-hc-ruler]');
    if (!ruler) return;
    if (event.key === 'ArrowUp' || event.key === 'ArrowRight') {
      event.preventDefault();
      this.stepRuler(ruler, 1);
    } else if (event.key === 'ArrowDown' || event.key === 'ArrowLeft') {
      event.preventDefault();
      this.stepRuler(ruler, -1);
    } else if (event.key === 'Home') {
      event.preventDefault();
      this.setRulerValue(ruler, Number(ruler.dataset.min));
    } else if (event.key === 'End') {
      event.preventDefault();
      this.setRulerValue(ruler, Number(ruler.dataset.max));
    }
  }

  setupDestinationPicker() {
    const picker = this.querySelector('[data-hc-destination-picker]');
    const nativeSelect = this.querySelector('[data-hc-dhl-zone]');
    const optionsRoot = picker?.querySelector('[data-hc-destination-options]');
    if (!picker || !nativeSelect || !optionsRoot || picker.dataset.ready === 'true') return;

    this.destinationPicker = picker;
    this.destinationNativeSelect = nativeSelect;
    this.destinationTrigger = picker.querySelector('[data-hc-destination-trigger]');
    this.destinationPanel = picker.querySelector('[data-hc-destination-panel]');
    this.destinationValue = picker.querySelector('[data-hc-destination-value]');
    this.destinationOptions = Array.from(nativeSelect.options)
      .filter((option) => option.value)
      .map((nativeOption, index) => {
        const option = document.createElement('button');
        option.type = 'button';
        option.className = 'hcp-destination-picker__option';
        option.dataset.hcDestinationOption = '';
        option.dataset.value = nativeOption.value;
        option.dataset.index = String(index);
        option.setAttribute('role', 'option');
        option.setAttribute('aria-selected', 'false');
        option.tabIndex = -1;
        option.innerHTML = `<span>${nativeOption.textContent}</span><small>${nativeOption.dataset.countryCode}</small>`;
        optionsRoot.append(option);
        return option;
      });

    picker.dataset.ready = 'true';
    this.classList.add('is-destination-enhanced');
    document.addEventListener('pointerdown', this.onDestinationDocumentPointerDown);
    this.syncDestinationPicker();
  }

  syncDestinationPicker() {
    if (!this.destinationNativeSelect || !this.destinationOptions) return;

    const selectedValue = this.destinationNativeSelect.value;
    const selectedOption = this.destinationNativeSelect.selectedOptions?.[0];
    if (this.destinationValue) {
      this.destinationValue.textContent = selectedOption?.textContent || 'Select destination';
    }

    this.destinationOptions.forEach((option) => {
      const isSelected = option.dataset.value === selectedValue;
      option.setAttribute('aria-selected', String(isSelected));
      option.classList.toggle('is-selected', isSelected);
    });
  }

  openDestinationPicker() {
    if (!this.destinationPicker || !this.destinationTrigger || !this.destinationPanel) return;

    this.destinationPicker.classList.add('is-open');
    this.destinationTrigger.setAttribute('aria-expanded', 'true');
    this.destinationPanel.setAttribute('aria-hidden', 'false');
    const selectedIndex = this.destinationOptions.findIndex((option) => option.classList.contains('is-selected'));
    this.destinationActiveIndex = selectedIndex >= 0 ? selectedIndex : 0;
    requestAnimationFrame(() => this.focusDestinationOption(this.destinationActiveIndex));
  }

  closeDestinationPicker({ restoreFocus = false } = {}) {
    if (!this.destinationPicker || !this.destinationTrigger || !this.destinationPanel) return;

    this.destinationPicker.classList.remove('is-open');
    this.destinationTrigger.setAttribute('aria-expanded', 'false');
    this.destinationPanel.setAttribute('aria-hidden', 'true');
    this.destinationOptions?.forEach((option) => {
      option.tabIndex = -1;
    });
    if (restoreFocus) this.destinationTrigger.focus({ preventScroll: true });
  }

  selectDestinationOption(option) {
    const nativeSelect = this.destinationNativeSelect;
    if (!nativeSelect || !option?.dataset.value) return;

    nativeSelect.value = option.dataset.value;
    nativeSelect.dispatchEvent(new Event('change', { bubbles: true }));
    this.syncDestinationPicker();
    this.closeDestinationPicker({ restoreFocus: true });
  }

  focusDestinationOption(index) {
    if (!this.destinationOptions?.length) return;

    const normalizedIndex = Math.max(0, Math.min(index, this.destinationOptions.length - 1));
    this.destinationActiveIndex = normalizedIndex;
    this.destinationOptions.forEach((option, optionIndex) => {
      option.tabIndex = optionIndex === normalizedIndex ? 0 : -1;
    });
    this.destinationOptions[normalizedIndex].focus({ preventScroll: true });
    this.destinationOptions[normalizedIndex].scrollIntoView({ block: 'nearest' });
  }

  handleDestinationKeyDown(event) {
    const picker = event.target.closest('[data-hc-destination-picker]');
    if (!picker || picker !== this.destinationPicker) return false;

    const isOpen = picker.classList.contains('is-open');
    const isTrigger = event.target.matches('[data-hc-destination-trigger]');
    const option = event.target.closest('[data-hc-destination-option]');

    if (isTrigger && (event.key === 'Enter' || event.key === ' ' || event.key === 'ArrowDown')) {
      event.preventDefault();
      this.openDestinationPicker();
      return true;
    }

    if (!isOpen) return false;
    if (event.key === 'Escape') {
      event.preventDefault();
      this.closeDestinationPicker({ restoreFocus: true });
      return true;
    }
    if (event.key === 'Tab') {
      this.closeDestinationPicker({ restoreFocus: false });
      return false;
    }
    if (option && (event.key === 'Enter' || event.key === ' ')) {
      event.preventDefault();
      this.selectDestinationOption(option);
      return true;
    }
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp' || event.key === 'Home' || event.key === 'End') {
      event.preventDefault();
      const nextIndex = event.key === 'Home'
        ? 0
        : event.key === 'End'
          ? this.destinationOptions.length - 1
          : this.destinationActiveIndex + (event.key === 'ArrowDown' ? 1 : -1);
      this.focusDestinationOption(nextIndex);
      return true;
    }

    return false;
  }

  onDestinationDocumentPointerDown(event) {
    if (!this.destinationPicker?.classList.contains('is-open')) return;
    if (!this.destinationPicker.contains(event.target)) {
      this.closeDestinationPicker({ restoreFocus: false });
    }
  }

  onPointerDown(event) {
    const galleryStage = event.target.closest('[data-hc-gallery-stage]');
    const galleryControl = event.target.closest('[data-hc-gallery-prev], [data-hc-gallery-next]');
    if (galleryStage && !galleryControl && event.isPrimary && event.button === 0) {
      this.gallerySwipeStart = {
        pointerId: event.pointerId,
        x: event.clientX,
        y: event.clientY,
      };
      return;
    }

    const track = event.target.closest('[data-hc-ruler-track]');
    const ruler = track?.closest('[data-hc-ruler]');
    if (!track || !ruler || event.button !== 0) return;
    event.preventDefault();
    if (ruler.dataset.hcMeasurePoint) {
      this.setActiveMeasurementPoint(ruler.dataset.hcMeasurePoint);
    }
    this.activeRuler = ruler;
    this.activeRulerTrack = track;
    this.activeRulerPointer = event.pointerId;
    track.setPointerCapture(event.pointerId);
    this.setRulerValue(ruler, this.valueFromPointer(ruler, event.clientX));
  }

  onPointerMove(event) {
    if (this.gallerySwipeStart?.pointerId === event.pointerId) {
      const deltaX = event.clientX - this.gallerySwipeStart.x;
      const deltaY = event.clientY - this.gallerySwipeStart.y;
      if (Math.abs(deltaY) > Math.abs(deltaX) && Math.abs(deltaY) > 16) {
        this.gallerySwipeStart = null;
      }
      return;
    }

    if (!this.activeRuler || event.pointerId !== this.activeRulerPointer) return;
    this.setRulerValue(this.activeRuler, this.valueFromPointer(this.activeRuler, event.clientX));
  }

  onPointerUp(event) {
    if (this.gallerySwipeStart?.pointerId === event.pointerId) {
      const swipeStart = this.gallerySwipeStart;
      this.gallerySwipeStart = null;
      if (event.type === 'pointercancel') return;

      const deltaX = event.clientX - swipeStart.x;
      const deltaY = event.clientY - swipeStart.y;
      if (Math.abs(deltaX) < 48 || Math.abs(deltaX) <= Math.abs(deltaY)) return;
      this.moveGallery(deltaX < 0 ? 1 : -1);
      return;
    }

    if (!this.activeRuler || event.pointerId !== this.activeRulerPointer) return;
    if (this.activeRulerTrack?.hasPointerCapture(event.pointerId)) {
      this.activeRulerTrack.releasePointerCapture(event.pointerId);
    }
    this.activeRuler = null;
    this.activeRulerTrack = null;
    this.activeRulerPointer = null;
  }

  onDialogClose() {
    if (this.activeDialogTrigger?.isConnected) this.activeDialogTrigger.focus();
    this.activeDialogTrigger = null;
  }

  async onSubmit(event) {
    const form = event.currentTarget;
    event.preventDefault();
    event.stopImmediatePropagation();

    // Pricing preparation is asynchronous. A click, Enter key, or delegated
    // theme handler can otherwise produce a second submit while the first one
    // is still waiting, creating two independent cart lines. Keep one request
    // owner from the initial submit until the cart update settles.
    if (this.addSubmissionLocked) return;
    this.addSubmissionLocked = true;
    this.pendingCartAdd = false;

    if (!this.validateDimensions()) {
      this.addSubmissionLocked = false;
      return;
    }
    const calculatorReady = await this.prepareOrderSubmission();
    if (!calculatorReady) {
      this.addSubmissionLocked = false;
      return;
    }

    const productForm = form?.closest('product-form-component');
    if (!productForm || typeof productForm.handleSubmit !== 'function') {
      this.addSubmissionLocked = false;
      return;
    }

    // This configurator owns the original submit while its asynchronous price
    // preparation runs. Hand the prepared form directly to Horizon's single
    // cart owner instead of replaying a second native submit event. Replaying
    // with requestSubmit() allowed two independent add requests to be created
    // by the custom capture listener and the theme component.
    this.pendingCartAdd = true;
    try {
      productForm.handleSubmit(event);
    } catch (error) {
      this.pendingCartAdd = false;
      this.addSubmissionLocked = false;
      throw error;
    }
  }

  async prepareOrderSubmission() {
    this.syncPricing();
    this.syncFreight();
    const calculatorReady = this.syncPriceCalculator();
    const error = this.querySelector('.hcp-form__error');

    const calculatorSettled = calculatorReady && await this.waitForPriceCalculator();
    if (!calculatorSettled) {
      if (error) {
        error.textContent = 'Your delivered price is still updating. Please try again.';
        error.classList.remove('hidden');
      }
      return false;
    }

    if (error) {
      error.textContent = '';
      error.classList.add('hidden');
    }
    this.updateEditConfigurationUrl();
    return true;
  }

  async waitForPriceCalculator({ timeout = 1200 } = {}) {
    const expectedTotalCents = Number(this.dataset.estimatedTotalCents);
    const areaInput = this.querySelector(
      'input.entangle-input[name="area"], input.entangle-lw-input-field[name="area"], input[name="area"]',
    );
    if (!Number.isFinite(expectedTotalCents) || !areaInput?.value) return false;

    const calculatorTotal = this.querySelector('#entangleTotalPrice');
    const startedAt = Date.now();
    let frames = 0;
    while (Date.now() - startedAt <= timeout) {
      await new Promise((resolve) => {
        if (typeof requestAnimationFrame === 'function') {
          requestAnimationFrame(() => resolve());
        } else {
          setTimeout(resolve, 16);
        }
      });
      frames += 1;

      if (!calculatorTotal) {
        if (frames >= 2) return true;
        continue;
      }

      const numericText = String(calculatorTotal.textContent || '').replace(/[^0-9.-]/g, '');
      const renderedTotalCents = Math.round(Number(numericText) * 100);
      if (Number.isFinite(renderedTotalCents) && Math.abs(renderedTotalCents - expectedTotalCents) <= 1) {
        return true;
      }
    }

    return false;
  }

  onCartLinesUpdate(event) {
    const ownsSubmission = event.action === 'add' && this.pendingCartAdd;
    const releaseSubmission = () => {
      if (!ownsSubmission) return;
      this.pendingCartAdd = false;
      this.addSubmissionLocked = false;
    };

    if (!event.promise) releaseSubmission();

    event.promise
      ?.then(({ cart, detail }) => {
        if (detail?.didError) return;
        this.syncCartCount(cart);
      })
      .catch(() => {})
      .finally(releaseSubmission);
  }

  syncCartCount(cart) {
    const quantity = Number(cart?.totalQuantity);
    if (!Number.isFinite(quantity)) return;

    this.querySelectorAll('[data-hc-nav-cart-count]').forEach((node) => {
      node.textContent = String(quantity);
    });
  }

  openCartDrawer() {
    const cartDrawer = document.querySelector('theme-drawer#cart-drawer');
    if (cartDrawer) cartDrawer.open();
  }

  startCheckoutNavigation(trigger) {
    trigger.classList.add('is-pending');
    trigger.setAttribute('aria-disabled', 'true');
    trigger.textContent = 'Opening checkout…';
  }

  formatCheckoutMoney(amount, currencyCode = this.dataset.hcCurrency || 'HKD') {
    return new Intl.NumberFormat(this.dataset.hcLocale || 'en-HK', {
      style: 'currency',
      currency: currencyCode,
      currencyDisplay: 'code',
    }).format(Number(amount) || 0);
  }

  syncDraftCheckoutSummary(dialog, cartPayload = null) {
    if (!dialog) return;
    const cartItem = cartPayload?.items?.[0];
    const cartProperties = cartItem?.properties || {};
    const value = (selector) => this.querySelector(selector)?.value || '';
    const setSummary = (key, text) => {
      const node = dialog.querySelector(`[data-hc-checkout-summary="${key}"]`);
      if (node) node.textContent = text;
    };
    const widthCm = Number(cartProperties['Width (cm)'] ?? value('[data-hc-dimension="width"]')) || 0;
    const heightCm = Number(cartProperties['Height (cm)'] ?? value('[data-hc-dimension="height"]')) || 0;
    const physicalArea = Number(cartProperties['Area (m²)'] ?? value('[data-hc-property="area-m2"]')) || (widthCm * heightCm / 10000);
    const billableArea = Math.max(1, Number(this.dataset.billableArea) || physicalArea || 1);
    const productTotal = ((Number(this.dataset.hcUnitPrice) || 0) * billableArea) / 100;
    const preview = dialog.querySelector('[data-hc-checkout-preview]');
    const previewUrl = cartProperties['_Preview image'] || cartItem?.image || value('[data-hc-property="preview-image"]');
    if (preview && previewUrl) preview.src = previewUrl;
    const directionIcon = dialog.querySelector('[data-hc-checkout-direction-icon]');
    const directionIconUrl = cartProperties['_Opening direction icon'] || value('[data-hc-property="direction-icon"]');
    const directionLabel = cartProperties['Opening Direction'] || value('[data-hc-property="direction"]');
    if (directionIcon) {
      if (directionIconUrl) directionIcon.src = directionIconUrl;
      if (directionLabel) directionIcon.alt = `Cellular shade configured for ${directionLabel.toLowerCase()} opening direction`;
    }
    setSummary('light-control', cartProperties['Light Control'] || value('[data-hc-property="series"]'));
    setSummary('fabric-color', cartProperties['Fabric Color'] || value('[data-hc-property="color"]'));
    setSummary('rail-finish', cartProperties['Rail Finish'] || value('[data-hc-property="rail"]'));
    setSummary('opening-direction', directionLabel);
    setSummary('width', `${Math.round(widthCm * 10)} mm`);
    setSummary('height', `${Math.round(heightCm * 10)} mm`);
    setSummary('area', `${physicalArea.toFixed(2)} m²`);
    const cartTotalCents = this.cartDeliveredTotalCents(cartPayload);
    const estimatedTotal = this.querySelector('[data-hc-estimated-total]')?.textContent?.trim();
    const displayTotal = Number.isFinite(cartTotalCents) && cartTotalCents > 0
      ? this.formatCheckoutMoney(cartTotalCents / 100, cartPayload?.currency || this.dataset.hcCurrency)
      : estimatedTotal && !/select destination/i.test(estimatedTotal)
        ? estimatedTotal
        : this.formatCheckoutMoney(productTotal);
    dialog.querySelectorAll('[data-hc-confirmed-total]').forEach((node) => {
      node.textContent = displayTotal;
    });
    const totalLabel = dialog.querySelector('[data-hc-checkout-total-label]');
    if (totalLabel) totalLabel.textContent = 'Estimated total · delivery included';
  }

  cartDeliveredTotalCents(cartPayload) {
    if (!Array.isArray(cartPayload?.items)) return NaN;
    const deliveredTotal = cartPayload.items.reduce((total, item) => {
      const quantity = Number(item.quantity) || 1;
      const deliveredUnit = Number(item.properties?.['_Estimated delivered total cents']) || 0;
      const freight = Number(item.properties?.['_Estimated freight cents']) || 0;
      const finalLine = Number(item.final_line_price) || 0;
      const originalLine = Number(item.original_line_price) || finalLine;
      const productDiscount = Math.max(0, originalLine - finalLine);
      if (deliveredUnit > 0) return total + deliveredUnit * quantity - productDiscount;
      return total + finalLine + freight * quantity;
    }, 0);
    return Math.max(0, deliveredTotal - (Number(cartPayload?.total_discount) || 0));
  }

  hasFaizDiscount() {
    try {
      return localStorage.getItem('faizDiscountApplied') === 'true';
    } catch {
      return false;
    }
  }

  openServerPricedCheckout({ cartPayload = null } = {}) {
    if (!this.dataset.hcCheckoutEndpoint) return false;
    const dialog = this.querySelector('[data-hc-draft-checkout-dialog]');
    this.cartCheckoutPayload = cartPayload;
    const cartCountry = cartPayload?.items?.map((item) => item.properties?.['_Shipping country code']).find(Boolean);
    const country = cartCountry || this.querySelector('[data-hc-dhl-zone]')?.selectedOptions?.[0]?.dataset.countryCode;
    if (!dialog || !country) {
      this.querySelector('[data-hc-dhl-zone]')?.focus();
      return true;
    }
    const countryInput = dialog.querySelector('[data-hc-address-country]');
    if (countryInput) countryInput.value = country;
    const countryName = dialog.querySelector('[data-hc-checkout-country-name]');
    const selectedDestination = this.querySelector('[data-hc-dhl-zone]')?.selectedOptions?.[0];
    if (countryName) countryName.value = selectedDestination?.textContent?.trim() || country;
    this.resetDraftCheckoutConfirmation();
    this.syncDraftCheckoutSummary(dialog, cartPayload);
    dialog.showModal();
    requestAnimationFrame(() => dialog.querySelector('input:not([type="hidden"])')?.focus());
    return true;
  }

  openCheckoutFromUrl() {
    if (typeof URL === 'undefined' || typeof window === 'undefined' || !window.location?.href) return false;
    const url = new URL(window.location.href);
    if (url.searchParams.get('hc_checkout') !== '1') return false;
    url.searchParams.delete('hc_checkout');
    window.history?.replaceState?.({}, '', `${url.pathname}${url.search}${url.hash}`);
    requestAnimationFrame(() => this.openServerPricedCheckout());
    return true;
  }

  resetDraftCheckoutConfirmation() {
    const dialog = this.querySelector('[data-hc-draft-checkout-dialog]');
    if (!dialog) return;
    dialog.dataset.confirmed = 'false';
    const confirmation = dialog.querySelector('[data-hc-address-confirmation]');
    if (confirmation) confirmation.hidden = true;
    const cartTotalCents = this.cartDeliveredTotalCents(this.cartCheckoutPayload);
    const estimatedTotal = Number.isFinite(cartTotalCents) && cartTotalCents > 0
      ? this.formatCheckoutMoney(cartTotalCents / 100, this.cartCheckoutPayload?.currency || this.dataset.hcCurrency)
      : this.querySelector('[data-hc-estimated-total]')?.textContent?.trim() || 'Select destination and size';
    dialog.querySelectorAll('[data-hc-confirmed-total]').forEach((node) => {
      node.textContent = estimatedTotal;
    });
    const totalLabel = dialog.querySelector('[data-hc-checkout-total-label]');
    if (totalLabel) totalLabel.textContent = 'Estimated total · delivery included';
    const submit = dialog.querySelector('[data-hc-address-submit]');
    if (submit) submit.hidden = false;
    if (submit) submit.textContent = 'Review address & final total →';
    const editorCheckoutLink = dialog.querySelector('[data-hc-editor-checkout-link]');
    if (editorCheckoutLink) {
      editorCheckoutLink.hidden = true;
      editorCheckoutLink.removeAttribute('href');
    }
    const error = dialog.querySelector('[data-hc-checkout-error]');
    if (error) error.hidden = true;
  }

  buildDraftCheckoutPayload(form = null) {
    const value = (selector) => this.querySelector(selector)?.value || '';
    const billableArea = Math.max(1, Number(this.dataset.billableArea) || 1);
    const unitPriceCents = Number(this.dataset.hcUnitPrice) || 0;
    const physicalArea = Number(value('[data-hc-property="area-m2"]')) || 0;
    const formData = form ? new FormData(form) : null;
    const variantId = value('[data-hc-product-form] input[name="id"]');
    const currencyCode = this.dataset.hcCurrency || 'HKD';
    const cart = this.cartCheckoutPayload;
    const lines = cart?.items?.length ? cart.items.map((item) => {
      const properties = item.properties || {};
      const widthCm = Number(properties['Width (cm)']);
      const heightCm = Number(properties['Height (cm)']);
      const physicalAreaM2 = Number(properties['Area (m²)']) || (widthCm * heightCm / 10000);
      return {
        variantId: `gid://shopify/ProductVariant/${item.variant_id}`,
        quantity: Number(item.quantity) || 1,
        unitPrice: { amount: (Number(item.final_price || item.price) / 100).toFixed(2), currencyCode: cart.currency || currencyCode },
        configuration: {
          lightControl: properties['Light Control'],
          fabricColor: properties['Fabric Color'],
          railFinish: properties['Rail Finish'],
          openingDirection: properties['Opening Direction'],
          widthCm,
          heightCm,
          physicalAreaM2,
          billedAreaM2: Math.max(1, physicalAreaM2),
          previewUrl: properties['_Preview image'] || item.image || '',
          directionIconUrl: properties['_Opening direction icon'] || '',
          editUrl: properties['_Edit configuration URL']
            ? new URL(properties['_Edit configuration URL'], window.location.origin).href
            : new URL(item.url, window.location.origin).href,
        },
      };
    }) : [{
      variantId: `gid://shopify/ProductVariant/${variantId}`,
      quantity: 1,
      unitPrice: { amount: ((unitPriceCents * billableArea) / 100).toFixed(2), currencyCode },
      configuration: {
        lightControl: value('[data-hc-property="series"]'),
        fabricColor: value('[data-hc-property="color"]'),
        railFinish: value('[data-hc-property="rail"]'),
        openingDirection: value('[data-hc-property="direction"]'),
        widthCm: Number(value('[data-hc-dimension="width"]')),
        heightCm: Number(value('[data-hc-dimension="height"]')),
        physicalAreaM2: physicalArea,
        billedAreaM2: billableArea,
        previewUrl: value('[data-hc-property="preview-image"]'),
        directionIconUrl: value('[data-hc-property="direction-icon"]'),
        editUrl: new URL(value('[data-hc-property="edit-url"]'), window.location.origin).href,
      },
    }];
    return {
      discountCode: this.hasFaizDiscount() ? 'FAIZ10' : '',
      email: formData?.get('email') || '',
      destination: {
        firstName: formData?.get('firstName') || '', lastName: formData?.get('lastName') || '',
        address1: formData?.get('address1') || '', address2: formData?.get('address2') || '',
        city: formData?.get('city') || '', province: formData?.get('province') || '', zip: formData?.get('zip') || '',
        phone: formData?.get('phone') || '',
        countryCode: formData?.get('countryCode')
          || this.querySelector('[data-hc-dhl-zone]')?.selectedOptions?.[0]?.dataset.countryCode
          || '',
      },
      lines,
    };
  }

  async onAddressSubmit(event) {
    event.preventDefault();
    const form = event.currentTarget;
    const dialog = this.querySelector('[data-hc-draft-checkout-dialog]');
    const submit = form.querySelector('[data-hc-address-submit]');
    const error = form.querySelector('[data-hc-checkout-error]');
    if (!form.reportValidity() || submit?.disabled) return;
    const confirmed = dialog?.dataset.confirmed === 'true';
    submit.disabled = true;
    submit.textContent = confirmed ? 'Preparing secure checkout…' : 'Checking delivery…';
    if (error) error.hidden = true;
    const requestController = new AbortController();
    const requestTimeout = window.setTimeout(() => requestController.abort(), 20000);
    try {
      const payload = this.buildDraftCheckoutPayload(form);
      const endpoint = this.dataset.hcCheckoutEndpoint.replace(/\/$/, '');
      const response = await fetch(`${endpoint}/${confirmed ? 'checkout' : 'quote'}`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        signal: requestController.signal,
        body: JSON.stringify(payload),
      });
      const contentType = response.headers.get('content-type') || '';
      let result;
      if (contentType.includes('application/json')) {
        result = await response.json();
      } else {
        const responseHtml = await response.text();
        const invoiceMatch = responseHtml.match(/https?:\/\/[^"'\s<>]+\/invoices\/[^"'\s<>]+/i);
        const redirectedInvoice = response.redirected && response.url.includes('/invoices/')
          ? response.url
          : invoiceMatch?.[0]?.replaceAll('&amp;', '&');
        result = redirectedInvoice
          ? { checkoutUrl: redirectedInvoice }
          : { error: 'Shopify secure checkout did not return a usable checkout link.' };
      }
      if (!response.ok) throw new Error(result.error || 'Delivery checkout could not be prepared.');
      if (confirmed) {
        if (!result.checkoutUrl) throw new Error(result.error || 'Shopify secure checkout link is missing.');
        const secureCheckoutUrl = this.normalizeSecureCheckoutUrl(result.checkoutUrl);
        if (this.isThemeEditorContext()) {
          submit.hidden = true;
          this.showEditorCheckoutLink(secureCheckoutUrl);
          return;
        }
        window.location.assign(secureCheckoutUrl);
        return;
      }
      const confirmedAddress = result.confirmedAddress || payload.destination;
      const address = [confirmedAddress.address1, confirmedAddress.address2, confirmedAddress.city, confirmedAddress.province, confirmedAddress.zip, confirmedAddress.countryCode]
        .filter(Boolean)
        .join(', ');
      const grandTotal = result.pricing.total;
      form.querySelector('[data-hc-confirmed-address]').textContent = address;
      form.querySelectorAll('[data-hc-confirmed-total]').forEach((node) => {
        node.textContent = this.formatCheckoutMoney(grandTotal.amount, grandTotal.currencyCode);
      });
      const totalLabel = form.querySelector('[data-hc-checkout-total-label]');
      if (totalLabel) totalLabel.textContent = 'Final total · delivery included';
      form.querySelector('[data-hc-address-confirmation]').hidden = false;
      dialog.dataset.confirmed = 'true';
      submit.textContent = 'Confirm & open secure checkout';
    } catch (requestError) {
      if (error) {
        error.textContent = requestError.name === 'AbortError'
          ? 'The connection is taking longer than expected. Please check your connection and try again.'
          : requestError.message;
        error.hidden = false;
      }
      submit.textContent = 'Try again';
    } finally {
      window.clearTimeout(requestTimeout);
      submit.disabled = false;
    }
  }

  normalizeSecureCheckoutUrl(checkoutUrl) {
    const secureUrl = new URL(checkoutUrl, window.location.origin);
    if (secureUrl.protocol !== 'https:') {
      throw new Error('Shopify secure checkout URL must use HTTPS.');
    }
    return secureUrl.href;
  }

  isThemeEditorContext() {
    return Boolean(window.Shopify?.designMode || window.self !== window.top);
  }

  showEditorCheckoutLink(checkoutUrl) {
    const link = this.querySelector('[data-hc-editor-checkout-link]');
    if (!link) return;
    link.href = checkoutUrl;
    link.hidden = false;
    link.focus({ preventScroll: true });
  }

  setupStickyCartObserver() {
    if (!this.cartActions || !this.stickyCart || !('IntersectionObserver' in window)) return;

    this.stickyCartPassedActions = false;
    this.onStickyCartViewportChange = () => this.syncStickyCartVisibility();
    const documentRoot = document.scrollingElement || document.documentElement;
    const initialScrollRoot = this.updateStickyCartScrollRoot();
    window.addEventListener('resize', this.onStickyCartViewportChange);
    this.stickyCartObserver = new IntersectionObserver(([entry]) => {
      this.stickyCartPassedActions = !entry.isIntersecting && entry.boundingClientRect.top < 0;
      this.syncStickyCartVisibility();
    }, {
      root: initialScrollRoot === documentRoot ? null : initialScrollRoot,
    });
    this.stickyCartObserver.observe(this.cartActions);
  }

  getStickyCartScrollRoot() {
    let ancestor = this.parentElement;

    while (ancestor && ancestor !== document.body) {
      const overflowY = window.getComputedStyle?.(ancestor)?.overflowY || '';
      const canScroll = /(auto|scroll|overlay)/.test(overflowY)
        && ancestor.scrollHeight > ancestor.clientHeight + 2;
      if (canScroll) return ancestor;
      ancestor = ancestor.parentElement;
    }

    return document.scrollingElement || document.documentElement;
  }

  prefersReducedMotion() {
    return window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true;
  }

  scrollToConfiguratorTop() {
    const target = this.matches('[data-hc-configurator-top]')
      ? this
      : this.querySelector('[data-hc-configurator-top]');
    if (!target) return;

    target.scrollIntoView({
      block: 'start',
      behavior: this.prefersReducedMotion() ? 'auto' : 'smooth',
    });
  }

  updateStickyCartScrollRoot() {
    const documentRoot = document.scrollingElement || document.documentElement;
    const nextRoot = this.getStickyCartScrollRoot();
    const nextTarget = nextRoot === documentRoot ? window : nextRoot;

    if (this.stickyCartScrollTarget !== nextTarget) {
      this.stickyCartScrollTarget?.removeEventListener('scroll', this.onStickyCartViewportChange);
      nextTarget.addEventListener('scroll', this.onStickyCartViewportChange, { passive: true });
      this.stickyCartScrollTarget = nextTarget;
    }

    this.stickyCartScrollRoot = nextRoot;
    return nextRoot;
  }

  syncStickyCartVisibility() {
    if (!this.stickyCart) return;

    const documentRoot = document.scrollingElement || document.documentElement;
    const root = this.updateStickyCartScrollRoot();
    const isDocumentRoot = root === documentRoot;
    const scrollTop = isDocumentRoot
      ? Math.max(window.scrollY || 0, root.scrollTop || 0)
      : root.scrollTop || 0;
    const viewportHeight = isDocumentRoot
      ? window.innerHeight || root.clientHeight
      : root.clientHeight;
    const viewportTop = isDocumentRoot ? 0 : root.getBoundingClientRect?.().top || 0;
    const cartActionsRect = this.cartActions?.getBoundingClientRect?.();
    if (cartActionsRect) {
      this.stickyCartPassedActions = cartActionsRect.bottom <= viewportTop;
    }
    const distanceToBottom = root.scrollHeight - (scrollTop + viewportHeight);
    const shouldShow = this.stickyCartPassedActions && distanceToBottom > 2;
    this.stickyCart.classList.toggle('is-visible', shouldShow);
    this.stickyCart.setAttribute('aria-hidden', String(!shouldShow));
  }

  syncStickyCart() {
    if (!this.stickyCart) return;

    const labels = [
      this.querySelector('[data-hc-series-label]')?.textContent.trim(),
      this.querySelector('[data-hc-color-label]')?.textContent.trim(),
      this.querySelector('[data-hc-rail-label]')?.textContent.trim(),
      this.querySelector('[data-hc-direction-label]')?.textContent.trim(),
    ].filter(Boolean);
    this.setText('[data-hc-sticky-selection]', labels.join(' · '));
  }

  syncSeries() {
    const selectedSeries = this.querySelector('[name="hc-series"]:checked');
    if (!selectedSeries) return;

    const series = selectedSeries.value;
    this.dataset.series = series;

    this.querySelectorAll('[data-hc-color-group]').forEach((group) => {
      const active = group.dataset.hcColorGroup === series;
      group.hidden = !active;
      group.querySelectorAll('input').forEach((input) => {
        input.disabled = !active;
      });

      if (active && !group.querySelector('input:checked')) {
        const firstInput = group.querySelector('input');
        if (firstInput) firstInput.checked = true;
      }
    });

    this.syncSelectedStates();
  }

  sourceFor(familyId, key) {
    return this.manifest.railOverrides?.[familyId]?.[key] || this.manifest.families[familyId]?.[key];
  }

  syncCheckoutVariant() {
    const input = this.querySelector('[data-hc-product-form] input[name="id"]');
    if (!input) return '';
    const fallbackVariantId = input.dataset.fallbackVariantId || input.value;
    if (!input.dataset.fallbackVariantId) input.dataset.fallbackVariantId = fallbackVariantId;
    const key = [this.dataset.family, this.dataset.rail, this.dataset.direction]
      .map((value) => String(value || '').trim().toLowerCase())
      .join('|');
    const mappedVariantId = this.manifest.checkoutVariants?.[key];
    input.value = String(mappedVariantId || fallbackVariantId).replace('gid://shopify/ProductVariant/', '');
    return input.value;
  }

  syncSelection() {
    const thumbnailRail = this.querySelector('.hcp-thumbnails');
    const thumbnailScrollTop = thumbnailRail?.scrollTop ?? 0;
    const thumbnailScrollLeft = thumbnailRail?.scrollLeft ?? 0;
    const series = this.dataset.series;
    const color = this.querySelector(`[data-hc-color-group="${series}"] input:checked`);
    const rail = this.querySelector('[name="hc-rail"]:checked');
    const direction = this.querySelector('[name="hc-direction"]:checked');
    if (!color || !rail || !direction) return;

    this.dataset.family = color.dataset.family;
    this.dataset.color = color.value;
    this.dataset.rail = rail.value;
    this.dataset.direction = direction.value;
    this.syncCheckoutVariant();

    this.syncSelectedStates();

    const family = this.manifest.families[this.dataset.family];
    if (!family) return;

    const seriesLabel = series === '93020' ? 'Light filtering' : 'Blackout';
    this.setText('[data-hc-series-label]', seriesLabel);
    this.setText('[data-hc-color-label]', color.dataset.label);
    this.setText('[data-hc-rail-label]', rail.dataset.label);
    this.setText('[data-hc-direction-label]', direction.dataset.label);

    this.setProperty('series', seriesLabel);
    this.setProperty('color', color.dataset.label);
    this.setProperty('rail', rail.dataset.label);
    this.setProperty('direction', direction.dataset.label);

    const fabricThumb = this.querySelector('[data-hc-fabric-thumb]');
    if (fabricThumb) {
      fabricThumb.src = this.manifest.fabricThumbnails?.[this.dataset.family] || family.fabric;
      fabricThumb.alt = `${color.dataset.label} ${series === '93020' ? 'light-filtering cellular fabric swatch for soft daylight' : 'total-blackout honeycomb fabric swatch for deeper sleep'}`;
    }

    const railSource = this.sourceFor(this.dataset.family, `rail-${this.dataset.rail}`);
    this.setProperty('preview-image', railSource || '');
    const railThumb = this.querySelector('[data-hc-rail-thumb]');
    if (railThumb) {
      railThumb.src = railSource;
      railThumb.alt = `${color.dataset.label} ${series === '93020' ? 'light-filtering cellular shade' : 'room-darkening honeycomb blind'} with ${rail.dataset.label} rail thumbnail`;
    }

    const directionSource = family[`direction-${this.dataset.direction}`];
    const directionIconSource = this.manifest.directionIcons?.[this.dataset.direction] || directionSource || '';
    this.setProperty('direction-icon', directionIconSource);
    this.setProperty('direction-code', this.dataset.direction);
    const directionThumb = this.querySelector('[data-hc-direction-thumb]');
    if (directionThumb) {
      directionThumb.src = directionSource;
      directionThumb.alt = `${direction.dataset.label} ${series === '93020' ? 'light-filtering cellular shade' : 'total-blackout honeycomb blind'} opening preview`;
    }

    if (thumbnailRail) {
      thumbnailRail.scrollTop = thumbnailScrollTop;
      thumbnailRail.scrollLeft = thumbnailScrollLeft;
    }
    const activeRole = this.dataset.mediaRole;
    if (['fabric', 'rail', 'direction'].includes(activeRole)) {
      this.showMedia(activeRole, 'selection');
    }
    this.syncStickyCart();
    this.preloadLikelyImages();
  }

  syncSelectedStates() {
    this.querySelectorAll('.hcp-segment, .hcp-swatch, .hcp-rail, .hcp-direction').forEach((label) => {
      label.classList.toggle('is-selected', Boolean(label.querySelector('input:checked')));
    });
  }

  preloadImage(source) {
    if (!source) return Promise.reject(new Error('Missing image source'));
    if (this.preloadCache.has(source)) return this.preloadCache.get(source);

    const request = new Promise((resolve, reject) => {
      const image = new Image();
      image.decoding = 'async';
      image.onload = () => resolve(source);
      image.onerror = () => {
        this.preloadCache.delete(source);
        reject(new Error(`Could not preload ${source}`));
      };
      image.src = source;
    });

    this.preloadCache.set(source, request);
    return request;
  }

  preloadLikelyImages() {
    const familyIds = Object.keys(this.manifest.families);
    const selectedFamilyId = this.dataset.family;
    const orderedFamilies = [selectedFamilyId, ...familyIds.filter((familyId) => familyId !== selectedFamilyId)];

    orderedFamilies.forEach((familyId) => {
      ['white', 'dark-grey', 'coffee-brown'].forEach((rail) => {
        const source = this.sourceFor(familyId, `rail-${rail}`);
        if (!source || this.preloadCache.has(source) || this.warmImageQueued.has(source)) return;
        this.warmImageQueued.add(source);
        this.warmImageQueue.push(source);
      });
    });

    this.scheduleImageWarmBatch();
  }

  scheduleImageWarmBatch() {
    if (this.preloadIdle || this.preloadTimer || this.warmImageQueue.length === 0) return;

    const warmBatch = () => {
      this.preloadIdle = null;
      this.preloadTimer = null;
      this.warmImageQueue.splice(0, 6).forEach((source) => {
        this.warmImageQueued.delete(source);
        this.preloadImage(source).catch(() => {});
      });
      if (this.warmImageQueue.length > 0) this.scheduleImageWarmBatch();
    };

    this.preloadTimer = setTimeout(warmBatch, 24);
  }

  prefetchSelectionIntent(event) {
    const input = event.target.closest('label')?.querySelector('input[type="radio"]')
      || event.target.closest('input[type="radio"]');
    if (!input) return;

    const isColor = input.name.startsWith('hc-color-');
    const isRail = input.name === 'hc-rail';
    if (!isColor && !isRail) return;

    const familyId = isColor ? input.dataset.family : this.dataset.family;
    const rail = isRail ? input.value : this.dataset.rail;
    const railSource = this.sourceFor(familyId, `rail-${rail}`);
    if (railSource) this.preloadImage(railSource).catch(() => {});

    if (isColor && this.dataset.mediaRole === 'fabric') {
      const fabricSource = this.manifest.families[familyId]?.fabric;
      if (fabricSource) this.preloadImage(fabricSource).catch(() => {});
    }
  }

  galleryDirectionFor(role) {
    const current = this.galleryRoles.indexOf(this.dataset.mediaRole);
    const target = this.galleryRoles.indexOf(role);
    return current >= 0 && target < current ? 'previous' : 'next';
  }

  moveGallery(step) {
    const current = Math.max(0, this.galleryRoles.indexOf(this.dataset.mediaRole));
    const next = (current + step + this.galleryRoles.length) % this.galleryRoles.length;
    this.showMedia(this.galleryRoles[next], step < 0 ? 'previous' : 'next');
  }

  async showMedia(role, direction = 'next') {
    const family = this.manifest.families[this.dataset.family];
    const series = this.dataset.series;
    const mainImage = this.querySelector('[data-hc-main-image]');
    if (!family || !mainImage) return;

    let source;
    let description;

    const selectedThumbnail = this.querySelector(`[data-hc-thumb-role="${role}"]`);
    const staticSource = selectedThumbnail?.dataset.hcMediaSource;

    if (staticSource) {
      source = staticSource;
      description = selectedThumbnail.dataset.hcMediaAlt || selectedThumbnail.getAttribute('aria-label') || 'Honeycomb shade interior view';
    } else if (role === 'fabric') {
      source = family.fabric;
      description = `${family.label} ${series === '93020' ? 'light-filtering cellular fabric detail for comfortable daylight' : 'total-blackout honeycomb fabric detail for uninterrupted sleep'}`;
    } else if (role === 'direction') {
      source = family[`direction-${this.dataset.direction}`];
      description = `${family.label} ${series === '93020' ? 'light-filtering cellular shade' : 'total-blackout honeycomb blind'} with ${this.labelFor('direction')} opening`;
    } else if (role === 'lifestyle') {
      source = this.manifest.lifestyle;
      description = 'Made-to-measure blackout honeycomb shade in a restful finished bedroom';
    } else {
      role = 'rail';
      source = this.sourceFor(this.dataset.family, `rail-${this.dataset.rail}`);
      description = `${family.label} ${series === '93020' ? 'light-filtering cellular shade' : 'room-darkening honeycomb blind'} with ${this.labelFor('rail')} aluminium rail`;
    }

    if (!source) return;
    this.dataset.mediaRole = role;
    this.setProperty('preview-image', source);

    let activeThumbnail;
    this.querySelectorAll('[data-hc-thumb-role]').forEach((button) => {
      const active = button.dataset.hcThumbRole === role;
      button.setAttribute('aria-pressed', String(active));
      button.setAttribute('aria-current', String(active));
      if (active) activeThumbnail = button;
    });
    const galleryStatus = this.querySelector('[data-hc-gallery-status]');
    if (galleryStatus && activeThumbnail) galleryStatus.textContent = activeThumbnail.getAttribute('aria-label') || description;

    if (mainImage.src === source || mainImage.currentSrc === source) {
      mainImage.alt = description;
      return;
    }

    const requestId = ++this.mediaRequest;
    mainImage.setAttribute('aria-busy', 'true');
    try {
      await this.preloadImage(source);
    } catch (error) {
      if (requestId === this.mediaRequest) mainImage.removeAttribute('aria-busy');
      return;
    }

    if (requestId !== this.mediaRequest) return;
    const shouldAnimate = direction === 'previous' || direction === 'next';
    if (!shouldAnimate) {
      mainImage.classList.remove('is-changing', 'is-entering-from-left', 'is-entering-from-right');
      mainImage.src = source;
      mainImage.alt = description;
      mainImage.removeAttribute('aria-busy');
      return;
    }

    const entryClass = direction === 'previous' ? 'is-entering-from-left' : 'is-entering-from-right';
    mainImage.classList.remove('is-changing', 'is-entering-from-left', 'is-entering-from-right');
    mainImage.classList.add('is-changing', entryClass);
    mainImage.src = source;
    mainImage.alt = description;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        mainImage.classList.remove('is-changing', entryClass);
        mainImage.removeAttribute('aria-busy');
      });
    });
  }

  openDialog(dialog, trigger) {
    if (!dialog || dialog.open) return;
    this.activeDialogTrigger = trigger;
    dialog.showModal();
    requestAnimationFrame(() => dialog.querySelector('[data-hc-close-dialog]')?.focus());
  }

  closeDialog(dialog) {
    if (!dialog?.open) return;
    dialog.close();
  }

  initializeRulers() {
    this.querySelectorAll('[data-hc-ruler]').forEach((ruler) => this.renderRuler(ruler));
    this.updateMeasurementSummary();
  }

  setUnit(unit) {
    if (!['millimeter', 'imperial'].includes(unit)) return;
    this.dataset.unit = unit;
    this.querySelectorAll('[data-hc-unit]').forEach((button) => {
      const selected = button.dataset.hcUnit === unit;
      button.classList.toggle('is-selected', selected);
      button.setAttribute('aria-pressed', String(selected));
    });
    this.querySelectorAll('[data-hc-dimension-unit]').forEach((label) => {
      label.textContent = unit === 'millimeter' ? 'mm' : 'ft · in · ⅛″';
    });
    this.querySelectorAll('[data-hc-ruler]').forEach((ruler) => this.renderRuler(ruler));
    this.updateMeasurementSummary();
  }

  stepRuler(ruler, direction) {
    if (!ruler || !direction) return;
    const current = Number(ruler.dataset.value);
    const metricStep = Number(ruler.dataset.step || 0.1);
    const step = this.dataset.unit === 'imperial' ? 2.54 / 8 : metricStep;
    this.setRulerValue(ruler, current + step * Math.sign(direction));
  }

  valueFromPointer(ruler, clientX) {
    const track = ruler.querySelector('[data-hc-ruler-track]');
    const rect = track.getBoundingClientRect();
    if (!rect.width) return Number(ruler.dataset.value);
    const ratio = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
    return Number(ruler.dataset.min) + ratio * (Number(ruler.dataset.max) - Number(ruler.dataset.min));
  }

  setRulerValue(ruler, value, sync = true) {
    if (!ruler || !Number.isFinite(value)) return;
    const minimum = Number(ruler.dataset.min);
    const maximum = Number(ruler.dataset.max);
    const metricStep = Number(ruler.dataset.step || 0.1);
    const step = this.dataset.unit === 'imperial' ? 2.54 / 8 : metricStep;
    const snapped = Math.round(value / step) * step;
    const next = Math.min(maximum, Math.max(minimum, snapped));
    ruler.dataset.value = String(Number(next.toFixed(4)));
    this.renderRuler(ruler);
    if (!sync) return;
    if (ruler.dataset.kind === 'main') this.syncDimensions();
    else this.updateMeasurementSummary();
  }

  renderRuler(ruler) {
    const value = Number(ruler.dataset.value);
    const minimum = Number(ruler.dataset.min);
    const maximum = Number(ruler.dataset.max);
    const percent = maximum === minimum ? 0 : ((value - minimum) / (maximum - minimum)) * 100;
    ruler.style.setProperty('--hcp-ruler-position', `${Math.min(100, Math.max(0, percent))}%`);
    this.setText('[data-hc-ruler-value]', this.formatDimension(value, true), ruler);
    const labels = [...ruler.querySelectorAll('[data-hc-ruler-scale] span')];
    const scaleCount = labels.length;
    labels.forEach((label, index) => {
      const ratio = index / Math.max(1, scaleCount - 1);
      const scaleValue = minimum + (maximum - minimum) * ratio;
      label.textContent = this.formatDimension(scaleValue, false);
    });
    ruler.querySelectorAll('[data-hc-step-label]').forEach((label) => {
      label.textContent = this.dataset.unit === 'imperial' ? '⅛″' : '1 mm';
    });
    ruler.setAttribute('aria-valuenow', String(Number(value.toFixed(4))));
    ruler.setAttribute('aria-valuetext', this.formatDimension(value, true));
    const decrease = ruler.querySelector('[data-hc-ruler-step="-1"]');
    const increase = ruler.querySelector('[data-hc-ruler-step="1"]');
    if (decrease) decrease.disabled = value <= minimum;
    if (increase) increase.disabled = value >= maximum;
    if (ruler.dataset.hcMeasurePoint) {
      this.updateDiagramPoint(ruler.dataset.hcMeasurePoint, value);
    }
  }

  updateDiagramPoint(key, value) {
    if (!key || !Number.isFinite(value)) return;
    const node = this.querySelector(`[data-hc-diagram-point="${key}"] strong`);
    if (node) node.textContent = this.formatDimension(value, true);
  }

  setActiveMeasurementPoint(key) {
    this.querySelectorAll('[data-hc-diagram-point]').forEach((node) => {
      node.classList.toggle('is-active', node.dataset.hcDiagramPoint === key);
    });
  }

  formatDimension(value, includeUnit = true) {
    if (this.dataset.unit === 'millimeter') return `${Math.round(value * 10)}${includeUnit ? ' mm' : ''}`;
    const totalEighths = Math.max(0, Math.round((value / 2.54) * 8));
    const feet = Math.floor(totalEighths / 96);
    const remainder = totalEighths - feet * 96;
    const inches = Math.floor(remainder / 8);
    const eighths = remainder % 8;
    const fractionGlyphs = ['', '⅛', '¼', '⅜', '½', '⅝', '¾', '⅞'];
    const fraction = fractionGlyphs[eighths];
    return `${feet}′ ${inches}${fraction}″`;
  }

  imperialToCm(feet, inches, eighths) {
    return ((feet * 12) + inches + (eighths / 8)) * 2.54;
  }

  syncDimensions() {
    const widthRuler = this.querySelector('[data-hc-main-measurement="width"] [data-hc-ruler]');
    const heightRuler = this.querySelector('[data-hc-main-measurement="height"] [data-hc-ruler]');
    const widthInput = this.querySelector('[data-hc-dimension="width"]');
    const heightInput = this.querySelector('[data-hc-dimension="height"]');
    if (!widthRuler || !heightRuler || !widthInput || !heightInput) return;
    widthInput.value = Number(widthRuler.dataset.value).toFixed(1);
    heightInput.value = Number(heightRuler.dataset.value).toFixed(1);
    this.syncPricing();
    this.syncFreight();
  }

  prepareMeasurementGuide() {
    const width = Number(this.querySelector('[data-hc-dimension="width"]')?.value || 100);
    const height = Number(this.querySelector('[data-hc-dimension="height"]')?.value || 100);
    this.querySelectorAll('[data-hc-measure-point^="width-"]').forEach((ruler) => {
      this.setRulerValue(ruler, width, false);
    });
    this.querySelectorAll('[data-hc-measure-point^="height-"]').forEach((ruler) => {
      this.setRulerValue(ruler, height, false);
    });
    this.updateMeasurementSummary();
    this.setActiveMeasurementPoint('width-left');
  }

  measurementValues(prefix) {
    return [...this.querySelectorAll(`[data-hc-measure-point^="${prefix}-"]`)].map((ruler) => Number(ruler.dataset.value));
  }

  updateMeasurementSummary() {
    const widths = this.measurementValues('width');
    const heights = this.measurementValues('height');
    if (!widths.length || !heights.length) return;
    const orderWidth = Math.min(...widths);
    const orderHeight = Math.min(...heights);
    const recess = Number(this.querySelector('[data-hc-recess-depth]')?.dataset.value || 0);
    this.setText('[data-hc-order-width]', this.formatDimension(orderWidth, true));
    this.setText('[data-hc-order-height]', this.formatDimension(orderHeight, true));
    this.setText('[data-hc-order-recess]', this.formatDimension(recess, true));
    [['width', orderWidth], ['height', orderHeight]].forEach(([axis, value]) => {
      const node = this.querySelector(`[data-hc-diagram-minimum="${axis}"]`);
      if (node) node.textContent = this.formatDimension(value, true);
    });
    const warning = this.querySelector('[data-hc-measure-warning]');
    if (warning) {
      const widthVariance = Math.max(...widths) - orderWidth;
      const heightVariance = Math.max(...heights) - orderHeight;
      warning.hidden = Math.max(widthVariance, heightVariance) <= 1.6;
    }
  }

  applyMeasurementGuide() {
    const widths = this.measurementValues('width');
    const heights = this.measurementValues('height');
    if (!widths.length || !heights.length) return;
    const width = Math.min(...widths);
    const height = Math.min(...heights);
    const widthRuler = this.querySelector('[data-hc-main-measurement="width"] [data-hc-ruler]');
    const heightRuler = this.querySelector('[data-hc-main-measurement="height"] [data-hc-ruler]');
    this.setRulerValue(widthRuler, width, false);
    this.setRulerValue(heightRuler, height, false);
    this.syncDimensions();
    this.closeDialog(this.querySelector('[data-hc-measurement-dialog]'));
  }

  validateDimensions() {
    const inputs = [...this.querySelectorAll('[data-hc-dimension]')];
    const invalidInput = inputs.find((input) => {
      const value = Number(input.value);
      return !(value >= Number(input.dataset.min) && value <= Number(input.dataset.max));
    });
    if (!invalidInput) return true;
    const ruler = this.querySelector(`[data-hc-main-measurement="${invalidInput.dataset.hcDimension}"] [data-hc-ruler]`);
    ruler?.focus();
    return false;
  }

  syncPricing() {
    const widthInput = this.querySelector('input[name="properties[Width (cm)]"]');
    const heightInput = this.querySelector('input[name="properties[Height (cm)]"]');
    const width = Number(widthInput?.value);
    const height = Number(heightInput?.value);

    if (!(width > 0 && height > 0)) {
      this.productSubtotalCents = null;
      delete this.dataset.billableArea;
      this.setText('[data-hc-price]', this.defaultPriceText);
      this.syncStickyCart();
      this.syncEstimatedTotal();
      return;
    }

    const physicalArea = (width * height) / 10000;
    const billableArea = Math.max(1, (width * height) / 10000);
    const areaValue = String(Number(billableArea.toFixed(4)));
    const physicalAreaValue = String(Number(physicalArea.toFixed(4)));
    const unitPrice = Number(this.dataset.hcUnitPrice || 0);
    const calculatedPrice = Math.round(unitPrice * billableArea);

    this.productSubtotalCents = calculatedPrice;
    this.dataset.billableArea = areaValue;
    this.setProperty('area-m2', physicalAreaValue);
    const totalAreaInput = this.querySelector('input[name="properties[Total Area]"]');
    if (totalAreaInput) totalAreaInput.value = physicalAreaValue;
    this.setText('[data-hc-price]', this.formatMoney(calculatedPrice));
    this.syncStickyCart();
    this.syncEstimatedTotal();
  }

  syncPriceCalculator() {
    const areaInput = this.querySelector(
      'input.entangle-input[name="area"], input.entangle-lw-input-field[name="area"], input[name="area"]',
    );
    if (!areaInput) return false;

    const billableArea = Number(this.dataset.billableArea);
    const unitPrice = Number(this.dataset.hcUnitPrice || 0);
    const freightEstimateCents = Number(this.freightEstimateCents);
    const hasExactTotal = billableArea > 0 && unitPrice > 0 && Number.isFinite(freightEstimateCents);

    if (!hasExactTotal) {
      if (areaInput.value !== '') {
        areaInput.value = '';
        areaInput.dispatchEvent(new Event('input', { bubbles: true }));
        areaInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
      return false;
    }

    const pricingUnits = Math.round((billableArea + (freightEstimateCents / unitPrice)) * 10000) / 10000;
    const areaValue = pricingUnits.toFixed(4);
    if (areaInput.value !== areaValue) {
      areaInput.value = areaValue;
      areaInput.min = '1';
      areaInput.step = '0.000001';
      areaInput.readOnly = true;
      areaInput.setAttribute('aria-label', 'Calculated made-to-measure total including estimated DHL freight');
      areaInput.dispatchEvent(new Event('input', { bubbles: true }));
      areaInput.dispatchEvent(new Event('change', { bubbles: true }));
    }
    const physicalAreaValue = this.querySelector('[data-hc-property="area-m2"]')?.value;
    const totalAreaInput = this.querySelector('input[name="properties[Total Area]"]');
    if (totalAreaInput && physicalAreaValue) totalAreaInput.value = physicalAreaValue;
    return true;
  }

  updateEditConfigurationUrl() {
    if (typeof URL === 'undefined' || typeof window === 'undefined') return;
    const url = new URL(window.location.href);
    const width = this.querySelector('[data-hc-dimension="width"]')?.value;
    const height = this.querySelector('[data-hc-dimension="height"]')?.value;
    const destination = this.querySelector('[data-hc-dhl-zone]')?.value;
    const values = {
      hc_series: this.dataset.series,
      hc_color: this.dataset.color,
      hc_rail: this.dataset.rail,
      hc_direction: this.dataset.direction,
      hc_width: width,
      hc_height: height,
      hc_destination: destination,
    };
    Object.entries(values).forEach(([key, value]) => {
      if (value) url.searchParams.set(key, value);
    });
    url.hash = 'configure';
    this.setProperty('edit-url', `${url.pathname}${url.search}${url.hash}`);
  }

  restoreConfigurationFromUrl() {
    if (typeof URL === 'undefined' || typeof window === 'undefined' || !window.location?.href) return false;
    const params = new URL(window.location.href).searchParams;
    const series = params.get('hc_series');
    const color = params.get('hc_color');
    const rail = params.get('hc_rail');
    const direction = params.get('hc_direction');
    const width = Number(params.get('hc_width'));
    const height = Number(params.get('hc_height'));
    const destination = params.get('hc_destination');
    if (!series && !color && !rail && !direction && !width && !height && !destination) return false;

    const safe = (value) => String(value || '').replace(/(["\\])/g, '\\$1');
    const seriesInput = series ? this.querySelector(`[data-hc-series="${safe(series)}"]`) : null;
    const colorInput = series && color
      ? this.querySelector(`[data-hc-color-group="${safe(series)}"] input[value="${safe(color)}"]`)
      : null;
    const railInput = rail ? this.querySelector(`[name="hc-rail"][value="${safe(rail)}"]`) : null;
    const directionInput = direction
      ? this.querySelector(`[name="hc-direction"][value="${safe(direction)}"]`)
      : null;
    if (seriesInput) seriesInput.checked = true;
    if (colorInput) colorInput.checked = true;
    if (railInput) railInput.checked = true;
    if (directionInput) directionInput.checked = true;

    const widthRuler = this.querySelector('[data-hc-main-measurement="width"] [data-hc-ruler]');
    const heightRuler = this.querySelector('[data-hc-main-measurement="height"] [data-hc-ruler]');
    if (width > 0 && widthRuler) this.setRulerValue(widthRuler, width, false);
    if (height > 0 && heightRuler) this.setRulerValue(heightRuler, height, false);
    const destinationSelect = this.querySelector('[data-hc-dhl-zone]');
    if (destination && destinationSelect) destinationSelect.value = destination;
    return true;
  }

  syncFreight() {
    const estimator = globalThis.HoneycombDhlEstimator;
    const width = Number(this.querySelector('input[name="properties[Width (cm)]"]')?.value);
    const height = Number(this.querySelector('input[name="properties[Height (cm)]"]')?.value);
    const zoneSelect = this.querySelector('[data-hc-dhl-zone]');
    const selectedOption = zoneSelect?.selectedOptions?.[0];
    const zone = Number(selectedOption?.dataset.zone);
    const countryCode = selectedOption?.dataset.countryCode || '';

    this.setProperty('shipping-country-code', countryCode);
    this.setProperty('dhl-zone', zone > 0 ? `Zone ${zone}` : '');

    if (!estimator || !(width > 0 && height > 0 && zone > 0)) {
      this.freightEstimateCents = null;
      this.setText('[data-hc-freight-price]', 'Select destination and size');
      this.setText('[data-hc-freight-weight]', '—');
      this.setText('[data-hc-freight-carton]', '—');
      this.setText(
        '[data-hc-freight-note]',
        'Final shipping is confirmed at checkout. Duties and taxes may apply.',
      );
      this.clearFreightProperties();
      this.syncEstimatedTotal();
      return;
    }

    const quote = estimator.estimate({
      widthCm: width,
      heightCm: height,
      zone,
      packageExtraCm: Number(this.dataset.hcDhlPackageExtra),
      cartonWidthCm: Number(this.dataset.hcDhlCartonWidth),
      cartonHeightCm: Number(this.dataset.hcDhlCartonHeight),
      baseWeightKg: Number(this.dataset.hcDhlBaseWeight),
      perimeterKgPerM: Number(this.dataset.hcDhlPerimeterWeight),
      areaKgPerM2: Number(this.dataset.hcDhlAreaWeight),
      fuelPercent: Number(this.dataset.hcDhlFuelPercent),
      bufferPercent: Number(this.dataset.hcDhlBufferPercent),
      cnyToStoreRate: Number(this.dataset.hcDhlCnyToStoreRate),
    });
    const carton = `${quote.outerLengthCm} × ${quote.cartonWidthCm} × ${quote.cartonHeightCm}`;

    this.setText('[data-hc-freight-weight]', `${quote.billableWeightKg} kg`);
    this.setText('[data-hc-freight-carton]', `${carton} cm`);
    this.setProperty('dhl-carton', carton);
    this.setProperty('dhl-weight', String(quote.billableWeightKg));

    if (quote.manualQuote) {
      this.freightEstimateCents = null;
      this.setText('[data-hc-freight-price]', 'Manual freight confirmation');
      this.setText(
        '[data-hc-freight-note]',
        'Shipping requires manual confirmation. We will confirm the final amount before production.',
      );
      this.setProperty('dhl-cny', 'Manual confirmation required');
      this.setProperty('dhl-store', 'Manual confirmation required');
      this.syncEstimatedTotal();
      return;
    }

    if (quote.baseFreightCny === null) {
      this.freightEstimateCents = null;
      this.setText('[data-hc-freight-price]', 'Rate unavailable');
      this.setText(
        '[data-hc-freight-note]',
        'Shipping requires manual confirmation. We will confirm the final amount before production.',
      );
      this.clearFreightProperties({ keepPackage: true });
      this.syncEstimatedTotal();
      return;
    }

    const fuelPercent = Number(this.dataset.hcDhlFuelPercent) || 0;
    const bufferPercent = Number(this.dataset.hcDhlBufferPercent) || 0;
    const storeCurrency = this.dataset.hcCurrency || 'HKD';

    if (this.dataset.hcCheckoutEndpoint) {
      this.freightEstimateCents = null;
      this.setText('[data-hc-estimated-total]', 'Updating total…');
      this.requestMarketEstimate();
      return;
    }

    this.freightEstimateCents = Math.round(quote.estimatedStoreCurrency * 100);
    this.setText('[data-hc-freight-price]', this.formatMoney(this.freightEstimateCents));
    this.setProperty('dhl-cny', `CNY ${quote.estimatedFreightCny.toFixed(2)}`);
    this.setProperty('dhl-store', `${storeCurrency} ${quote.estimatedStoreCurrency.toFixed(2)}`);
    this.syncEstimatedTotal();
  }

  requestMarketEstimate() {
    if (this.marketEstimateTimer) clearTimeout(this.marketEstimateTimer);
    this.marketEstimateController?.abort();
    const controller = new AbortController();
    this.marketEstimateController = controller;
    this.marketEstimateTimer = setTimeout(async () => {
      try {
        const endpoint = this.dataset.hcCheckoutEndpoint.replace(/\/$/, '');
        const response = await fetch(`${endpoint}/estimate`, {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify(this.buildDraftCheckoutPayload()),
          signal: controller.signal,
        });
        const result = await response.json();
        if (!response.ok) throw new Error(result.error || 'The delivered total could not be updated.');
        const product = result.pricing?.product;
        const delivery = result.pricing?.delivery;
        const total = result.pricing?.total;
        if (!product || !delivery || !total) {
          throw new Error('Shopify did not return complete market pricing.');
        }
        const currencies = new Set([product.currencyCode, delivery.currencyCode, total.currencyCode]);
        if (currencies.size !== 1 || currencies.has(undefined)) {
          throw new Error('Shopify returned inconsistent market currencies.');
        }
        const marketCurrency = total.currencyCode;
        this.dataset.hcMarketCurrency = marketCurrency;
        this.productSubtotalCents = Math.round(Number(product.amount) * 100);
        this.freightEstimateCents = Math.round(Number(delivery.amount) * 100);
        this.dataset.estimatedTotalCents = String(Math.round(Number(total.amount) * 100));
        this.setProperty('estimated-freight-cents', String(Math.round(Number(delivery.amount) * 100)));
        this.setProperty('estimated-total-cents', String(Math.round(Number(total.amount) * 100)));
        this.setText('[data-hc-price]', this.formatCheckoutMoney(product.amount, marketCurrency));
        this.setText('[data-hc-freight-price]', this.formatCheckoutMoney(delivery.amount, marketCurrency));
        this.setText('[data-hc-estimated-total]', this.formatCheckoutMoney(total.amount, marketCurrency));
        this.setProperty('dhl-store', `${delivery.currencyCode} ${Number(delivery.amount).toFixed(2)}`);
        if (marketCurrency === (this.dataset.hcCurrency || 'HKD')) this.syncPriceCalculator();
      } catch (error) {
        if (error.name === 'AbortError') return;
        this.freightEstimateCents = null;
        delete this.dataset.estimatedTotalCents;
        this.setProperty('estimated-freight-cents', '');
        this.setProperty('estimated-total-cents', '');
        this.setText('[data-hc-estimated-total]', 'Total unavailable · try again');
        this.syncPriceCalculator();
      }
    }, 120);
  }

  syncEstimatedTotal() {
    const productSubtotalCents = this.productSubtotalCents;
    const freightEstimateCents = this.freightEstimateCents;

    if (!Number.isFinite(productSubtotalCents) || !Number.isFinite(freightEstimateCents)) {
      this.setText('[data-hc-estimated-total]', 'Select destination and size');
      delete this.dataset.estimatedTotalCents;
      this.syncPriceCalculator();
      return;
    }

    const billableArea = Number(this.dataset.billableArea);
    const unitPrice = Number(this.dataset.hcUnitPrice || 0);
    const pricingUnits = Math.round((billableArea + (freightEstimateCents / unitPrice)) * 10000) / 10000;
    const estimatedTotalCents = Math.round(unitPrice * pricingUnits);
    const chargedFreightCents = estimatedTotalCents - productSubtotalCents;
    this.dataset.estimatedTotalCents = String(estimatedTotalCents);
    this.setProperty('estimated-freight-cents', String(chargedFreightCents));
    this.setProperty('estimated-total-cents', String(estimatedTotalCents));
    this.setText('[data-hc-freight-price]', this.formatMoney(chargedFreightCents));
    this.setText('[data-hc-estimated-total]', this.formatMoney(estimatedTotalCents));
    this.syncPriceCalculator();
  }

  clearFreightProperties({ keepPackage = false } = {}) {
    if (!keepPackage) {
      this.setProperty('dhl-carton', '');
      this.setProperty('dhl-weight', '');
    }
    this.setProperty('dhl-cny', '');
    this.setProperty('dhl-store', '');
  }

  formatCny(amount) {
    return new Intl.NumberFormat('zh-CN', {
      style: 'currency',
      currency: 'CNY',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  }

  formatMoney(cents) {
    const currency = this.dataset.hcCurrency || 'HKD';
    const locale = this.dataset.hcLocale || 'en-HK';
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
      currencyDisplay: 'code',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(cents / 100);
  }

  setText(selector, value, root = this) {
    const node = root.querySelector(selector);
    if (node) node.textContent = value;
  }

  setProperty(name, value) {
    const input = this.querySelector(`[data-hc-property="${name}"]`);
    if (input) input.value = value;
  }

  labelFor(name) {
    return this.querySelector(`[name="hc-${name}"]:checked`)?.dataset.label || '';
  }
}

if (!customElements.get('honeycomb-configurator')) {
  customElements.define('honeycomb-configurator', HoneycombConfigurator);
}
})();
